import { metaOf, closureReason } from '../../lib/tasks';

/*
  The five queues of the Review Hub, and what each row says.

  Written 5 September 2026 for job 11, to Group E's five approved drawings.

  **The Review Hub was a dialog with five tabs in it.** It becomes a page under
  Inventory Counts, by the owner's decision of 31 August, and each tab becomes a
  queue on that page. That reverses a decision recorded in
  `docs/design/ATRIUM-REDESIGN-HANDOFF.md` (deleted 12 September 2026; kept in the tag before-phase-3-cleanup), deliberately.

  **Why it had to stop being a dialog.** On a phone the dialog squeezed a
  location name into about 90 pixels beside two buttons, so
  "SAT-CLEAR SPRING PHYSICAL THERAPY SOUTH" took five lines and its date took
  two more. Photographed before anything was changed:
  `docs/design/live/admin-overview-open-390.png` (deleted 12 September 2026; kept in the tag before-phase-3-cleanup). The page is the list and panel
  shape Group D settled, which does not have the problem.

  Everything here is a plain function of a row, so the words on a queue can be
  tested without rendering anything.
*/

export const QUEUES = [
  { key: 'counts',        label: 'Counts',        tone: 'app' },
  { key: 'edit_requests', label: 'Edit requests', tone: 'gold' },
  { key: 'closures',      label: 'Closures',      tone: 'ns' },
  { key: 'picked_up',     label: 'Picked up',     tone: 'blue' },
  { key: 'ownership',     label: 'Ownership',     tone: 'transfer' },
];

// The territory code, then the name. Rule 16, and the same shape every list in
// OnHand uses.
export function rowName(row, queue) {
  if (queue === 'counts') {
    return {
      code: row.warehouse?.region_prefix || '',
      name: row.warehouse?.display_name || row.warehouse?.name || 'A location',
    };
  }
  const meta = metaOf(row);
  const full = row.warehouse?.name || meta.location_name || 'A location';
  const dash = full.indexOf('-');
  return row.warehouse?.region_prefix
    ? { code: row.warehouse.region_prefix, name: row.warehouse.display_name || full }
    : { code: dash > 0 ? full.slice(0, dash) : '', name: dash > 0 ? full.slice(dash + 1) : full };
}

/*
  The quiet second line under a name in the queue: who, when, and the one fact
  that decides whether this row is the one you want.
*/
export function rowSays(row, queue, whenShort, lines) {
  const meta = metaOf(row);
  const when = whenShort ? whenShort(row.created_at || row.submitted_at) : '';
  switch (queue) {
    case 'counts': {
      /*
        **The rep and how big the sheet is**, which is what the drawing shows
        and what actually decides which one an administrator opens first. The
        date is on every row in this queue and is the same date on all 69 of
        them in the test bench, so it separates nothing.
      */
      const size = lines?.total
        ? lines.total + ' line' + (lines.total === 1 ? '' : 's')
        : 'No lines on this sheet';
      return [row.rep?.full_name || meta.rep_name || 'Nobody', size].filter(Boolean).join(' - ');
    }
    case 'closures':
      return [meta.rep_name || '', when, closureReason(meta.reason)].filter(Boolean).join(' - ');
    case 'ownership':
      return [
        (meta.requesting_rep_name || 'Somebody') + ' asking',
        (meta.current_owner_name || 'somebody') + ' holds it',
        when,
      ].filter(Boolean).join(' - ');
    case 'edit_requests':
    case 'picked_up':
    default:
      return [meta.rep_name || '', when].filter(Boolean).join(' - ');
  }
}

/*
  **What the two buttons at the bottom of the panel say and do.**

  The words are the owner's, settled in Group E's third round. "Reject" is gone:
  nothing is rejected, the sheet goes back to the rep who corrects it and submits
  it again, so the button says **Send back**. And the button that actually sends
  it names the person, because a button that names somebody is hard to press by
  accident.
*/
export function actionsFor(queue, row, names = {}) {
  const meta = metaOf(row);
  const first = n => String(n || '').trim().split(/\s+/)[0] || 'them';
  switch (queue) {
    case 'counts':
      return {
        away: 'Send back',
        act:  'Approve',
        actTone: 'app',
        says: 'Approving locks the sheet as the count of record. Sending it back reopens it for '
          + first(names.rep) + ', who corrects it and submits it again.',
      };
    case 'edit_requests':
      return {
        away: 'Deny',
        act:  'Approve',
        actTone: 'app',
        says: 'Approving reopens the sheet for ' + first(meta.rep_name) + '. It comes back through this queue when it is '
          + 'submitted again. Denying keeps it locked, and ' + first(meta.rep_name) + ' sees what you write.',
      };
    case 'closures':
      return {
        away: 'Deny',
        act:  'Confirm closure',
        actTone: 'ns',
        says: 'Confirming closes the location for good. It stops getting count sheets and comes off '
          + first(meta.rep_name) + "'s list.",
      };
    case 'picked_up':
      return {
        away: 'Take it back off ' + first(meta.rep_name),
        act:  'Mark reviewed',
        actTone: 'blue',
        says: 'Nothing here needs a decision. The location is already on ' + first(meta.rep_name)
          + "'s list and its sheet already exists. You are being told, so that a location nobody was "
          + 'given does not get counted by surprise.',
        aside: 'Picked up by mistake? Taking it back unassigns ' + first(meta.rep_name)
          + ' and leaves the location for somebody else. The sheet stays, with anything they have counted still on it.',
      };
    case 'ownership':
    default:
      return {
        away: 'Deny',
        act:  'Transfer to ' + first(meta.requesting_rep_name),
        actTone: 'transfer',
        says: 'Transferring hands the sheet to ' + first(meta.requesting_rep_name) + ' straight away, with everything '
          + first(meta.current_owner_name) + ' has counted kept on it. ' + first(meta.current_owner_name)
          + ' loses it the moment you press.',
      };
  }
}
